import type { AccountId } from "@repo/app-types/entities/account";
import type {
  AttainmentTrendMaxPeriod,
  AttainmentTrendResult,
  TeammateAttainmentTrends,
} from "@repo/app-types/entities/attainmentTrend";
import type { ComputedFieldDefinitionId } from "@repo/app-types/entities/computedField";
import type { CustomField } from "@repo/app-types/entities/customField";
import type {
  DateRange,
  ISODateTime,
  PlaybookScope,
  TeamPerformancePageFilter,
} from "@repo/app-types/entities/filters";
import type { GroupId } from "@repo/app-types/entities/group";
import type { InteractionMomentAdminApp } from "@repo/app-types/entities/interactionMoment";
import type { ResolvedKbAdherenceThemesData } from "@repo/app-types/entities/kbAdherenceThemes";
import type {
  PlaybookCriterionId,
  PlaybookId,
  PlaybookSectionId,
} from "@repo/app-types/entities/playbook";
import type {
  PlaybookReportPeriodSummary,
  PlaybookReportResponse,
  PlaybookReportType,
} from "@repo/app-types/entities/playbookReport";
import {
  textHighlightSchema,
  type TextHighlight,
} from "@repo/app-types/entities/recording";
import type {
  BasicTeammate,
  TeammateId,
} from "@repo/app-types/entities/teammate";
import type { TeammateAccountCallOutline } from "@repo/app-types/entities/teammateAccountCallOutline";
import type {
  GlobalAverageScorePerCriteria,
  GradingId,
  GradingsByGroupSort,
  GradingsByTeammateSort,
  HighImpactCriteriaResponse,
  MedianAverageScorePerCriteria,
  OutcomeDistributionByCriterionAndScore,
  ResolvedTeamPerformancePageRow,
  TeamBenchmarkScores,
  TeammateAccountGrading,
  TeammateAccountGradingFeedback,
  TeammateAccountGradingWithRecordings,
  TeammatePlaybookCriteriaScore,
  TeammatePlaybookKnowledgeBaseAdherenceScore,
  TeammatePlaybookToolUsageAccuracyScore,
  TeammateWithCriteriaAverageScore,
  TeamPerformanceByGroupRow,
  TranscriptMatchesByGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type {
  CoachingGoalCallsResult,
  TeammatePlaybookCoaching,
  TeammatePlaybookCoachingPeriodSummary,
} from "@repo/app-types/entities/teammatePlaybookCoaching";
import type {
  CoachingManagerInputsResult,
  TeammatePlaybookCoachingManagerInput,
} from "@repo/app-types/entities/teammatePlaybookCoachingManagerInput";
import type { Paragraph } from "@repo/app-types/helpers/gradeValidationHelpers";
import { safeParseJSONWithSchema } from "@repo/app-types/helpers/zodHelpers";
import { getBrowserTimeZone } from "../../helpers/utilities.js";
import {
  buildListTag,
  getTagsProvidedByEntity,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";
import { streamQuery } from "../streamQuery.js";
import { upsertBasicTeammateQueryData } from "./teammate.js";

export interface TopExampleTeammate extends BasicTeammate {
  average_score: number;
  /**
   * Daily average-score sparkline in chronological order, ending at the most
   * recent day. `null` entries mark days with no data; the whole series is
   * absent for rows with no trend data.
   */
  average_score_sparkline?: (number | null)[];
}

export interface PlaybookCriterionTopExample {
  grading: TeammateAccountGradingWithRecordings;
  teammate: TopExampleTeammate;
}

interface PerformanceAchievementTeammateMetrics {
  readonly playbook_adherence_percentile: number | null;
  readonly playbook_adherence_score: number | null;
  readonly outcome_conversion_percentile: number | null;
  readonly outcome_conversion_score: number | null;
  readonly nb_perfect_calls: number;
  readonly nb_calls: number;
}

export interface PerformanceAchievementQuarter {
  readonly start_date: string;
  readonly end_date: string;
  readonly nb_teammates: number;
  readonly nb_qualifying_calls: number;
  readonly nb_qualified_teammates: number;
  readonly team_avg_playbook_adherence_score: number | null;
  readonly team_playbook_adherence_percentiles: readonly {
    p: number;
    v: number;
  }[];
  readonly team_avg_outcome_conversion_score: number | null;
  readonly achievements: PerformanceAchievementTeammateMetrics;
}

export interface DashboardTimeMetricsResult {
  readonly totalCallCount: number;
  readonly averageCallLengthSeconds: number | null;
  readonly averageCallLengthPerGreenOutcomeSeconds: number | null;
  readonly callVolumeTrend: readonly number[];
  readonly averageCallLengthTrendSeconds: readonly (number | null)[];
  readonly averageCallLengthPerGreenOutcomeTrendSeconds: readonly (
    | number
    | null
  )[];
}

export interface PlaybookTeamPerformancePageApiResponse {
  data: {
    rows: readonly ResolvedTeamPerformancePageRow[];
    benchmark_scores?: TeamBenchmarkScores;
    benchmark_section_scores?: MedianAverageScorePerCriteria[];
  };
  next_cursor: string | null | undefined;
}

export interface PlaybookTeamPerformanceByGroupPageApiResponse {
  data: {
    rows: readonly TeamPerformanceByGroupRow[];
    benchmark_scores?: TeamBenchmarkScores;
    benchmark_section_scores?: MedianAverageScorePerCriteria[];
  };
  next_cursor: string | null | undefined;
}

export interface PlaybookTeammatePerformanceApiResponse {
  data: {
    teammate: ResolvedTeamPerformancePageRow;
    benchmark_scores?: TeamBenchmarkScores;
  };
}

export interface PlaybookTeammateCriteriaScoresApiResponse {
  data: {
    grading_count?: number;
    playbook_criteria_scores: TeammatePlaybookCriteriaScore[];
    green_rate?: number | null;
    value_rate_sparklines?: Record<string, (number | null)[]> | null;
    playbook_knowledge_base_adherence_score?: TeammatePlaybookKnowledgeBaseAdherenceScore | null;
    playbook_tool_usage_accuracy_scores?: TeammatePlaybookToolUsageAccuracyScore[];
  };
}

export interface PlaybookTeammateCriterionCoverageSecondsApiResponse {
  data: {
    criterion_average_seconds: Record<string, number>;
    top_performer_criterion_average_seconds: Record<string, number>;
  };
}

export interface PlaybookGlobalAverageScorePerCriteriaApiResponse {
  data: { average_score_per_criteria: GlobalAverageScorePerCriteria[] };
}

export interface PlaybookTeammateCoachingPeriodsApiResponse {
  data: {
    coaching_periods: TeammatePlaybookCoachingPeriodSummary[];
  };
}

export interface PlaybookTeammateCoachingApiResponse {
  data: { coaching: TeammatePlaybookCoaching };
}

export interface PlaybookTeammateCoachingGoalCallsApiResponse {
  data: CoachingGoalCallsResult;
}

export interface PlaybookTeammateCoachingManagerInputsApiResponse {
  data: CoachingManagerInputsResult;
}

export const performancePlaybookApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Team performance page
    playbookTeamPerformancePage: builder.infiniteQuery<
      {
        rows: readonly ResolvedTeamPerformancePageRow[];
        benchmarkScores?: TeamBenchmarkScores;
        benchmarkSectionScores?: MedianAverageScorePerCriteria[];
        nextCursor: string | null;
      },
      {
        playbookId: PlaybookId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
        /** Which section IDs to include scores from in the result (both teammate scores and aggregate "benchmark" scores). */
        playbookSectionIds?: PlaybookSectionId[];
        /** Computed field ids the client wants projected as columns. */
        computedFieldIds?: ComputedFieldDefinitionId[];
        sort?: GradingsByTeammateSort;
        limit?: number;
        /** Include each row's coaching goal progress (goal-progress column). */
        includeCoachingGoalProgress?: boolean;
      },
      string | null,
      PlaybookTeamPerformancePageApiResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.playbookId)}`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
          playbookSectionIds: queryArg.playbookSectionIds,
          computedFieldIds: queryArg.computedFieldIds,
          sort: queryArg.sort,
          cursor: pageParam,
          limit: queryArg.limit,
          includeCoachingGoalProgress: queryArg.includeCoachingGoalProgress,
        },
      }),
      transformResponse: (response) => ({
        rows: response.data.rows,
        benchmarkScores: response.data.benchmark_scores,
        benchmarkSectionScores: response.data.benchmark_section_scores,
        nextCursor: response.next_cursor ?? null,
      }),
    }),

    playbookTeamPerformanceByGroupPage: builder.infiniteQuery<
      {
        rows: readonly TeamPerformanceByGroupRow[];
        benchmarkScores?: TeamBenchmarkScores;
        benchmarkSectionScores?: MedianAverageScorePerCriteria[];
        nextCursor: string | null;
      },
      {
        playbookId: PlaybookId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
        playbookSectionIds?: PlaybookSectionId[];
        computedFieldIds?: ComputedFieldDefinitionId[];
        sort?: GradingsByGroupSort;
        limit?: number;
      },
      string | null,
      PlaybookTeamPerformanceByGroupPageApiResponse
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.playbookId)}/groups`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
          playbookSectionIds: queryArg.playbookSectionIds,
          computedFieldIds: queryArg.computedFieldIds,
          sort: queryArg.sort,
          cursor: pageParam,
          limit: queryArg.limit,
        },
      }),
      transformResponse: (response) => ({
        rows: response.data.rows,
        benchmarkScores: response.data.benchmark_scores,
        benchmarkSectionScores: response.data.benchmark_section_scores,
        nextCursor: response.next_cursor ?? null,
      }),
    }),

    playbookAllMostRecentGradingsCount: builder.query<
      number,
      {
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
        search?: string;
        deepSearchEnabled?: boolean;
        asOf?: ISODateTime;
      },
      { data: { recent_gradings_count: number } }
    >({
      query: (queryArg) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.scope.playbookId)}/recent_gradings_count`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
          search: queryArg.search,
          deepSearchEnabled: queryArg.deepSearchEnabled,
          asOf: queryArg.asOf,
        },
      }),
      transformResponse: (response) => response.data.recent_gradings_count,
    }),

    playbookAllMostRecentGradings: builder.infiniteQuery<
      {
        recentGradings: readonly TeammateAccountGrading[];
        basicTeammates: readonly BasicTeammate[];
        snippets: TranscriptMatchesByGrading;
        nextCursor: string | null;
      },
      {
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
        search?: string;
        deepSearchEnabled?: boolean;
        asOf?: ISODateTime;
      },
      string | null,
      {
        data: {
          recent_gradings: readonly TeammateAccountGrading[];
          basic_teammates: readonly BasicTeammate[];
          snippets?: TranscriptMatchesByGrading;
        };
        next_cursor: string | null | undefined;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.scope.playbookId)}/recent_gradings`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
          search: queryArg.search,
          deepSearchEnabled: queryArg.deepSearchEnabled,
          asOf: queryArg.asOf,
          cursor: pageParam,
        },
      }),
      transformResponse: (response) => ({
        recentGradings: response.data.recent_gradings,
        basicTeammates: response.data.basic_teammates,
        snippets: response.data.snippets ?? {},
        nextCursor: response.next_cursor ?? null,
      }),
      onQueryStarted: async (_queryArg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        const lastPage = data.pages.at(-1);
        const basicTeammates = lastPage?.basicTeammates ?? [];
        await Promise.all(
          basicTeammates.map(async (teammate) => {
            await dispatch(upsertBasicTeammateQueryData(teammate));
          }),
        );
      },
      providesTags: (result) => {
        if (!result) {
          return [];
        }

        const listTag = buildListTag(TagType.TeammatePerformanceGrading, []);
        const entityTags = result.pages
          .flatMap((page) => page.recentGradings)
          .map((row) => buildTeammatePerformanceGradingCacheTag(row.id));
        return [listTag, ...entityTags];
      },
    }),

    playbookTeammatePlaybooks: builder.query<
      readonly PlaybookId[],
      { teammateId: TeammateId; dateRange: DateRange },
      { data: { playbook_ids: readonly PlaybookId[] } }
    >({
      query: ({ teammateId, dateRange }) => ({
        method: "GET",
        url: `/performance/playbooks/teammates/${encodeURIComponent(teammateId)}/playbooks`,
        params: {
          after: dateRange.after,
          before: dateRange.before,
          time_zone: getBrowserTimeZone(),
        },
      }),
      transformResponse: (response) => response.data.playbook_ids,
      providesTags: (_result, _error, { teammateId }) =>
        getTagsProvidedByEntity(TagType.TeammatePerformance, teammateId),
    }),

    playbookTeammatePerformance: builder.query<
      ResolvedTeamPerformancePageRow & {
        benchmarkScores?: TeamBenchmarkScores;
      },
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      PlaybookTeammatePerformanceApiResponse
    >({
      query: (queryArg) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.playbookId)}/teammates/${encodeURIComponent(queryArg.teammateId)}/scores`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
        },
      }),
      transformResponse: (response) => ({
        ...response.data.teammate,
        benchmarkScores: response.data.benchmark_scores,
      }),
      providesTags: (_result, _error, { teammateId }) =>
        getTagsProvidedByEntity(TagType.TeammatePerformance, teammateId),
    }),

    playbookTeammatePlaybookCriteriaScores: builder.query<
      {
        gradingCount?: number;
        criteriaScores: TeammatePlaybookCriteriaScore[];
        /**
         * Win rate (0-1). `null` when the outcome is inapplicable (playbook
         * has no outcome definition); `undefined` when there's no outcome
         * data in the filtered period.
         */
        outcomeGreenRate: number | null | undefined;
        outcomeValueRateSparklines:
          | Record<string, (number | null)[]>
          | null
          | undefined;
        knowledgeBaseScore:
          | TeammatePlaybookKnowledgeBaseAdherenceScore
          | null
          | undefined;
        toolUsageAccuracyScores:
          | TeammatePlaybookToolUsageAccuracyScore[]
          | undefined;
      },
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      PlaybookTeammateCriteriaScoresApiResponse
    >({
      query: ({ playbookId, teammateId, scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/playbook-criteria-scores`,
        body: { scope, filters },
      }),
      transformResponse: (response) => ({
        gradingCount: response.data.grading_count,
        criteriaScores: response.data.playbook_criteria_scores,
        outcomeGreenRate: response.data.green_rate,
        outcomeValueRateSparklines: response.data.value_rate_sparklines,
        knowledgeBaseScore:
          response.data.playbook_knowledge_base_adherence_score,
        toolUsageAccuracyScores:
          response.data.playbook_tool_usage_accuracy_scores,
      }),
      providesTags: (_result, _error, { teammateId }) =>
        getTagsProvidedByEntity(
          TagType.TeammatePlaybookCriteriaScores,
          teammateId,
        ),
    }),

    playbookTeammatePlaybookCriterionCoverageSeconds: builder.query<
      {
        criterionAverageSeconds: Record<string, number>;
        topPerformerCriterionAverageSeconds: Record<string, number>;
      },
      {
        teammateId: TeammateId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      PlaybookTeammateCriterionCoverageSecondsApiResponse
    >({
      query: ({ teammateId, scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(scope.playbookId)}/teammates/${encodeURIComponent(teammateId)}/playbook-criterion-coverage-seconds`,
        body: { scope, filters },
      }),
      transformResponse: (response) => ({
        criterionAverageSeconds: response.data.criterion_average_seconds,
        topPerformerCriterionAverageSeconds:
          response.data.top_performer_criterion_average_seconds,
      }),
      providesTags: (_result, _error, { teammateId }) =>
        getTagsProvidedByEntity(
          TagType.TeammatePlaybookCriteriaScores,
          teammateId,
        ),
    }),

    playbookCriterionCoverageSeconds: builder.query<
      {
        criterionAverageSeconds: Record<string, number>;
        topPerformerCriterionAverageSeconds: Record<string, number>;
        criterionCallCount: Record<string, number>;
        totalRecordingDurationSeconds: number;
        windowDays: number;
      },
      {
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      {
        data: {
          criterion_average_seconds: Record<string, number>;
          top_performer_criterion_average_seconds: Record<string, number>;
          criterion_call_count: Record<string, number>;
          total_recording_duration_seconds: number;
          window_days: number;
        };
      }
    >({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(scope.playbookId)}/criterion-coverage-seconds`,
        body: { scope, filters },
      }),
      transformResponse: (response) => ({
        criterionAverageSeconds: response.data.criterion_average_seconds,
        topPerformerCriterionAverageSeconds:
          response.data.top_performer_criterion_average_seconds,
        criterionCallCount: response.data.criterion_call_count,
        totalRecordingDurationSeconds:
          response.data.total_recording_duration_seconds,
        windowDays: response.data.window_days,
      }),
    }),

    playbookTeammateMostRecentKbAdherenceThemes: builder.query<
      ResolvedKbAdherenceThemesData | null,
      { playbookId: PlaybookId; teammateId: TeammateId },
      {
        data: {
          kb_adherence_themes: ResolvedKbAdherenceThemesData | null;
        };
      }
    >({
      query: ({ playbookId, teammateId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/most-recent-kb-adherence-themes`,
      }),
      transformResponse: (response) => response.data.kb_adherence_themes,
    }),

    playbookTeammateCoachingPeriods: builder.query<
      TeammatePlaybookCoachingPeriodSummary[],
      { playbookId: PlaybookId; teammateId: TeammateId },
      PlaybookTeammateCoachingPeriodsApiResponse
    >({
      query: ({ playbookId, teammateId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/coaching-periods`,
      }),
      transformResponse: (response) => response.data.coaching_periods,
    }),

    playbookTeammateCoaching: builder.query<
      TeammatePlaybookCoaching,
      { playbookId: PlaybookId; teammateId: TeammateId; periodId?: string },
      PlaybookTeammateCoachingApiResponse
    >({
      query: ({ playbookId, teammateId, periodId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/coaching`,
        params: { period_id: periodId },
      }),
      transformResponse: (response) => response.data.coaching,
    }),

    playbookTeammateCoachingGoalCalls: builder.query<
      CoachingGoalCallsResult,
      { playbookId: PlaybookId; teammateId: TeammateId; periodId: string },
      PlaybookTeammateCoachingGoalCallsApiResponse
    >({
      query: ({ playbookId, teammateId, periodId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/coaching-goal-calls`,
        params: { period_id: periodId },
      }),
      transformResponse: (response) => response.data,
      providesTags: (result) => {
        if (!result) {
          return [];
        }
        // Mirror the recent-gradings tags so grade edits (reassess, feedback,
        // validation) refetch the window calls without a manual refresh.
        const listTag = buildListTag(TagType.TeammatePerformanceGrading, []);
        const entityTags = result.calls.map((call) =>
          buildTeammatePerformanceGradingCacheTag(call.grading_id),
        );
        return [listTag, ...entityTags];
      },
    }),

    playbookTeammateCoachingManagerInputs: builder.query<
      CoachingManagerInputsResult,
      { playbookId: PlaybookId; teammateId: TeammateId },
      PlaybookTeammateCoachingManagerInputsApiResponse
    >({
      query: ({ playbookId, teammateId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/coaching-manager-inputs`,
      }),
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, { playbookId, teammateId }) => [
        buildListTag(TagType.TeammateCoachingManagerInput, [
          playbookId,
          teammateId,
        ]),
      ],
    }),

    // Saves the manager input for a period: the upcoming week's goal when
    // criterionId is set, otherwise the current week's note.
    playbookTeammateSaveCoachingManagerInput: builder.mutation<
      TeammatePlaybookCoachingManagerInput,
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        periodId: string;
        criterionId?: string;
        note?: string;
      },
      { data: { manager_input: TeammatePlaybookCoachingManagerInput } }
    >({
      query: ({ playbookId, teammateId, periodId, criterionId, note }) => ({
        method: "PUT",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/coaching-manager-input`,
        body: { period_id: periodId, criterion_id: criterionId, note },
      }),
      transformResponse: (response) => response.data.manager_input,
      invalidatesTags: (_result, _error, { playbookId, teammateId }) => [
        buildListTag(TagType.TeammateCoachingManagerInput, [
          playbookId,
          teammateId,
        ]),
      ],
    }),

    // Deletes the manager input for a period: the whole goal for the upcoming
    // week ("Let Alpharun choose"), or just the note for the current week.
    playbookTeammateDeleteCoachingManagerInput: builder.mutation<
      void,
      { playbookId: PlaybookId; teammateId: TeammateId; periodId: string }
    >({
      query: ({ playbookId, teammateId, periodId }) => ({
        method: "DELETE",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/coaching-manager-input`,
        params: { period_id: periodId },
      }),
      invalidatesTags: (_result, _error, { playbookId, teammateId }) => [
        buildListTag(TagType.TeammateCoachingManagerInput, [
          playbookId,
          teammateId,
        ]),
      ],
    }),

    playbookGlobalAverageScorePerCriteria: builder.query<
      GlobalAverageScorePerCriteria[],
      {
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      PlaybookGlobalAverageScorePerCriteriaApiResponse
    >({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(scope.playbookId)}/global-average-score-per-criteria`,
        body: { scope, filters },
      }),
      transformResponse: (response) => response.data.average_score_per_criteria,
    }),

    // Aggregate outcome distribution per criteria for the whole team
    playbookOutcomeDistributionPerCriteria: builder.query<
      OutcomeDistributionByCriterionAndScore | null,
      {
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      {
        data: {
          criteria_distribution: OutcomeDistributionByCriterionAndScore | null;
        };
      }
    >({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(scope.playbookId)}/outcome-criteria-distribution`,
        body: { scope, filters },
      }),
      transformResponse: (response) => response.data.criteria_distribution,
    }),

    dashboardPlaybookAttainmentTrend: builder.query<
      AttainmentTrendResult,
      {
        playbookId: PlaybookId;
        groupIds: readonly GroupId[] | null;
        maxPeriod?: AttainmentTrendMaxPeriod;
      },
      { data: { playbook_attainment_trend: AttainmentTrendResult } }
    >({
      query: ({ playbookId, groupIds, maxPeriod }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/playbook-attainment-trend`,
        params: {
          group_id: groupIds ?? undefined,
          clickhouse: "true",
          max_period: maxPeriod,
          time_zone: getBrowserTimeZone(),
        },
      }),
      transformResponse: (response) => response.data.playbook_attainment_trend,
    }),

    dashboardPlaybookOutcomeAttainmentTrend: builder.query<
      AttainmentTrendResult | null,
      {
        playbookId: PlaybookId;
        groupIds: readonly GroupId[] | null;
        maxPeriod?: AttainmentTrendMaxPeriod;
      },
      {
        data: {
          playbook_outcome_attainment_trend: AttainmentTrendResult | null;
        };
      }
    >({
      query: ({ playbookId, groupIds, maxPeriod }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/playbook-outcome-attainment-trend`,
        params: {
          group_id: groupIds ?? undefined,
          clickhouse: "true",
          max_period: maxPeriod,
          time_zone: getBrowserTimeZone(),
        },
      }),
      transformResponse: (response) =>
        response.data.playbook_outcome_attainment_trend,
    }),

    playbookDashboardTimeMetrics: builder.query<
      DashboardTimeMetricsResult,
      {
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      { data: { dashboard_time_metrics: DashboardTimeMetricsResult } }
    >({
      query: ({ scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(scope.playbookId)}/dashboard-time-metrics`,
        body: { scope, filters },
      }),
      transformResponse: (response) => response.data.dashboard_time_metrics,
    }),

    playbookAverageScorePerTeammateForCriteria: builder.query<
      TeammateWithCriteriaAverageScore[],
      {
        criteriaNodeId: string;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
      },
      {
        data: {
          teammates_with_criteria_average_score: TeammateWithCriteriaAverageScore[];
        };
      }
    >({
      query: ({ criteriaNodeId, scope, filters }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(scope.playbookId)}/average-score-per-teammate/${encodeURIComponent(criteriaNodeId)}`,
        body: { scope, filters },
      }),
      transformResponse: (response) =>
        response.data.teammates_with_criteria_average_score,
    }),

    playbookCriteriaAttainmentTrend: builder.query<
      Record<TeammateId, TeammateAttainmentTrends>,
      {
        playbookId: PlaybookId;
        criteriaNodeId: string;
        groupIds: readonly GroupId[] | null;
        maxPeriod?: AttainmentTrendMaxPeriod;
      },
      {
        data: {
          criteria_attainment_trend: Record<
            TeammateId,
            TeammateAttainmentTrends
          >;
        };
      }
    >({
      query: ({ playbookId, criteriaNodeId, groupIds, maxPeriod }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/criteria/${encodeURIComponent(criteriaNodeId)}/attainment-trend-per-teammate`,
        params: {
          group_id: groupIds ?? undefined,
          clickhouse: "true",
          max_period: maxPeriod,
          time_zone: getBrowserTimeZone(),
        },
      }),
      transformResponse: (response) => response.data.criteria_attainment_trend,
    }),

    playbookCriterionTopExamples: builder.query<
      PlaybookCriterionTopExample[],
      {
        playbookId: PlaybookId;
        criterionId: string;
        excludeTeammateId?: TeammateId;
      },
      { data: { top_examples: PlaybookCriterionTopExample[] } }
    >({
      query: ({ playbookId, criterionId, excludeTeammateId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/criteria/${encodeURIComponent(criterionId)}/top-examples`,
        params: {
          exclude_teammate_id: excludeTeammateId ?? undefined,
        },
      }),
      transformResponse: (response) => response.data.top_examples,
    }),

    playbookSectionAttainmentTrend: builder.query<
      Record<TeammateId, TeammateAttainmentTrends>,
      {
        playbookId: PlaybookId;
        sectionNodeId: string;
        groupIds: readonly GroupId[] | null;
        maxPeriod?: AttainmentTrendMaxPeriod;
      },
      {
        data: {
          section_attainment_trend: Record<
            TeammateId,
            TeammateAttainmentTrends
          >;
        };
      }
    >({
      query: ({ playbookId, sectionNodeId, groupIds, maxPeriod }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/sections/${encodeURIComponent(sectionNodeId)}/attainment-trend-per-teammate`,
        params: {
          group_id: groupIds ?? undefined,
          clickhouse: "true",
          max_period: maxPeriod,
          time_zone: getBrowserTimeZone(),
        },
      }),
      transformResponse: (response) => response.data.section_attainment_trend,
    }),

    playbookTeammateMostRecentGradingsCount: builder.query<
      number,
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
        search?: string;
        deepSearchEnabled?: boolean;
        asOf?: ISODateTime;
      },
      { data: { recent_gradings_count: number } }
    >({
      query: (queryArg) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.playbookId)}/teammates/${encodeURIComponent(queryArg.teammateId)}/recent-gradings-count`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
          search: queryArg.search,
          deepSearchEnabled: queryArg.deepSearchEnabled,
          asOf: queryArg.asOf,
        },
      }),
      transformResponse: (response) => response.data.recent_gradings_count,
    }),

    playbookTeammateMostRecentGradings: builder.infiniteQuery<
      {
        recentGradings: readonly TeammateAccountGrading[];
        snippets: TranscriptMatchesByGrading;
        nextCursor: string | null;
      },
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        scope: PlaybookScope;
        filters: readonly TeamPerformancePageFilter[];
        search?: string;
        deepSearchEnabled?: boolean;
        asOf?: ISODateTime;
      },
      string | null,
      {
        data: {
          recent_gradings: readonly TeammateAccountGrading[];
          snippets?: TranscriptMatchesByGrading;
        };
        next_cursor: string | null | undefined;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getPreviousPageParam: () => null,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
      query: ({ queryArg, pageParam }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(queryArg.playbookId)}/teammates/${encodeURIComponent(queryArg.teammateId)}/recent-gradings`,
        body: {
          scope: queryArg.scope,
          filters: queryArg.filters,
          search: queryArg.search,
          deepSearchEnabled: queryArg.deepSearchEnabled,
          asOf: queryArg.asOf,
          cursor: pageParam,
        },
      }),
      transformResponse: (response) => ({
        recentGradings: response.data.recent_gradings,
        snippets: response.data.snippets ?? {},
        nextCursor: response.next_cursor ?? null,
      }),
      providesTags: (result) => {
        if (!result) {
          return [];
        }

        // Building custom entity tags because we won't always have the grading id when invalidating in other RTK queries.
        const listTag = buildListTag(TagType.TeammatePerformanceGrading, []);
        const entityTags = result.pages
          .flatMap((page) => page.recentGradings)
          .map((row) => buildTeammatePerformanceGradingCacheTag(row.id));
        return [listTag, ...entityTags];
      },
    }),

    playbookTeammateGrading: builder.query<
      TeammateAccountGradingWithRecordings,
      { playbookId: PlaybookId; teammateId: TeammateId; accountId: AccountId },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ playbookId, teammateId, accountId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}`,
      }),
      transformResponse: (response) => response.data.grading,
      providesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookGradingById: builder.query<
      TeammateAccountGradingWithRecordings,
      { playbookId: PlaybookId; gradingId: GradingId },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ playbookId, gradingId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/gradings/${encodeURIComponent(gradingId)}`,
      }),
      transformResponse: (response) => response.data.grading,
      providesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookOtherTeammateAccountGradingsForAccount: builder.query<
      {
        otherGradings: readonly TeammateAccountGradingWithRecordings[];
        basicTeammates: readonly BasicTeammate[];
      },
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        accountId: AccountId;
        limit?: number;
      },
      {
        data: {
          other_gradings: readonly TeammateAccountGradingWithRecordings[];
          basic_teammates: readonly BasicTeammate[];
        };
      }
    >({
      query: ({ playbookId, teammateId, accountId, limit }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/other-gradings`,
        params: {
          limit: limit ?? undefined,
        },
      }),
      transformResponse: (response) => ({
        otherGradings: response.data.other_gradings,
        basicTeammates: response.data.basic_teammates,
      }),
      providesTags: (result) =>
        result
          ? result.otherGradings.map((g) =>
              buildTeammatePerformanceGradingCacheTag(g.id),
            )
          : [],
    }),

    playbookTeammateReassessGrading: builder.mutation<
      TeammateAccountGradingWithRecordings,
      { playbookId: PlaybookId; teammateId: TeammateId; accountId: AccountId },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ playbookId, teammateId, accountId }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/reassess`,
      }),
      transformResponse: (response) => response.data.grading,
      invalidatesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookTeammateSaveGradingValidation: builder.mutation<
      void,
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        accountId: AccountId;
        sectionOrCriteriaId: string;
        paragraphs: Paragraph[];
      }
    >({
      query: ({
        playbookId,
        teammateId,
        accountId,
        sectionOrCriteriaId,
        paragraphs,
      }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/save-grade-validation`,
        body: {
          section_or_criteria_id: sectionOrCriteriaId,
          paragraphs,
        },
      }),
      // No grading id in the response (void), so invalidate the whole list.
      invalidatesTags: [buildListTag(TagType.TeammatePerformanceGrading, [])],
    }),

    playbookTeammateAccountVoiceAgentCallFeedback: builder.mutation<
      TeammateAccountGradingWithRecordings,
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        accountId: AccountId;
        feedback: TeammateAccountGradingFeedback["voice_agent_call"];
      },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ playbookId, teammateId, accountId, feedback }) => ({
        method: "POST",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/voice-agent-call-feedback`,
        body: feedback,
      }),
      transformResponse: (response) => response.data.grading,
      invalidatesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookTeammateValidateGrade: streamQuery<
      { validation: string },
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        accountId: AccountId;
        sectionOrCriteriaId: string;
      }
    >(builder, {
      paramsToPath({ playbookId, teammateId, accountId }) {
        return `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/validate-grade-stream`;
      },
      paramsToBody({ sectionOrCriteriaId }) {
        return { section_or_criteria_id: sectionOrCriteriaId };
      },
      initialState: { validation: "" },
      updateState(data, chunk) {
        if (typeof chunk !== "string") {
          throw new Error("Expected chunk to be a string");
        }

        return { validation: data.validation + chunk };
      },
      keepUnusedDataFor: 300,
    }),

    playbookTeammateHighlightGrade: streamQuery<
      { rawJSON: string; highlights: TextHighlight[] },
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        accountId: AccountId;
        gradingValidation: string;
      }
    >(builder, {
      paramsToPath({ playbookId, teammateId, accountId }) {
        return `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/grade-highlighting-stream`;
      },
      paramsToBody({ gradingValidation }) {
        return { grading_validation: gradingValidation };
      },
      initialState: { rawJSON: "", highlights: [] },
      updateState(data, chunk) {
        if (typeof chunk !== "string") {
          throw new Error("Expected chunk to be a string");
        }

        const rawJSON = data.rawJSON + chunk;
        const rawJSONSkip = rawJSON.slice(1);

        // Naive partial JSON parsing.
        const highlights: TextHighlight[] = [];
        const highlightRegex = /\{(?:[^{}"\\]|\\.|"(?:\\.|[^"\\])*")*\}/g;

        let match: RegExpExecArray | null;
        while ((match = highlightRegex.exec(rawJSONSkip))) {
          const parsedMatch = safeParseJSONWithSchema(
            match[0],
            textHighlightSchema,
          );
          if (!parsedMatch) {
            continue;
          }

          highlights.push(parsedMatch);
        }

        return {
          rawJSON,
          highlights,
        };
      },
      // Cache highlighting results for 5 minutes per recording/validation combination
      keepUnusedDataFor: 300,
    }),

    playbookTeammateCallOutline: builder.query<
      TeammateAccountCallOutline,
      { playbookId: PlaybookId; teammateId: TeammateId; accountId: AccountId },
      { data: { call_outline: TeammateAccountCallOutline } }
    >({
      query: ({ playbookId, teammateId, accountId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/call-outline`,
      }),
      transformResponse: (response) => response.data.call_outline,
      providesTags: (_result, _error, { teammateId, accountId }) => [
        {
          type: TagType.TeammateCallOutline,
          id: `${teammateId}/${accountId}`,
        },
      ],
    }),

    playbookTeammateAccountGradingCustomFields: builder.query<
      CustomField[],
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        accountId: AccountId;
      },
      { data: { custom_fields: CustomField[] } }
    >({
      query: ({ playbookId, teammateId, accountId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/custom-fields`,
      }),
      transformResponse: (response) => response.data.custom_fields,
    }),

    // ========================================================================
    // Playbook Report Endpoints
    // ========================================================================

    playbookReportPeriods: builder.query<
      PlaybookReportPeriodSummary[],
      { playbookId: PlaybookId; reportType: PlaybookReportType },
      { data: { report_periods: PlaybookReportPeriodSummary[] } }
    >({
      query: ({ playbookId, reportType }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/reports/${encodeURIComponent(reportType)}`,
      }),
      transformResponse: (response) => response.data.report_periods,
    }),

    playbookHighImpactCriteria: builder.query<
      HighImpactCriteriaResponse,
      {
        playbookId: PlaybookId;
        groupIds?: readonly GroupId[] | null;
        asOfDateIso: string;
      },
      { data: HighImpactCriteriaResponse }
    >({
      query: ({ playbookId, groupIds, asOfDateIso }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/high-impact-criteria`,
        params: {
          group_id: groupIds ?? undefined,
          date: asOfDateIso,
          clickhouse: "true",
        },
      }),
      transformResponse: (response) => response.data,
    }),

    teammateHighImpactCriteria: builder.query<
      HighImpactCriteriaResponse,
      {
        playbookId: PlaybookId;
        teammateId: TeammateId;
        asOfDateIso: string;
      },
      { data: HighImpactCriteriaResponse }
    >({
      query: ({ playbookId, teammateId, asOfDateIso }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/high-impact-criteria`,
        params: {
          date: asOfDateIso,
          clickhouse: "true",
        },
      }),
      transformResponse: (response) => response.data,
    }),

    dashboardCriteriaAttainmentTrends: builder.query<
      Record<TeammateId, Record<string, TeammateAttainmentTrends>>,
      {
        playbookId: PlaybookId;
        groupIds: readonly GroupId[] | null;
        criterionIds?: readonly PlaybookCriterionId[];
        maxPeriod?: AttainmentTrendMaxPeriod;
      },
      {
        data: {
          criteria_attainment_trends: Record<
            TeammateId,
            Record<string, TeammateAttainmentTrends>
          >;
        };
      }
    >({
      query: ({ playbookId, groupIds, criterionIds, maxPeriod }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/criteria/attainment-trends`,
        params: {
          group_id: groupIds ?? undefined,
          criterion_ids: criterionIds,
          clickhouse: "true",
          max_period: maxPeriod,
          time_zone: getBrowserTimeZone(),
        },
      }),
      transformResponse: (response) => response.data.criteria_attainment_trends,
    }),

    playbookTeammatePerformanceAchievements: builder.query<
      readonly PerformanceAchievementQuarter[],
      { playbookId: PlaybookId; teammateId: TeammateId },
      {
        data: {
          achievements: readonly PerformanceAchievementQuarter[];
        };
      }
    >({
      query: ({ playbookId, teammateId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/achievements`,
      }),
      transformResponse: (response) => response.data.achievements,
      providesTags: (_result, _error, { teammateId }) =>
        getTagsProvidedByEntity(TagType.TeammatePerformance, teammateId),
    }),

    playbookTeammateInteractionMoments: builder.query<
      readonly InteractionMomentAdminApp[],
      { playbookId: PlaybookId; teammateId: TeammateId; accountId: AccountId },
      { data: { moments: readonly InteractionMomentAdminApp[] } }
    >({
      query: ({ playbookId, teammateId, accountId }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/teammates/${encodeURIComponent(teammateId)}/accounts/${encodeURIComponent(accountId)}/interaction-moments`,
      }),
      transformResponse: (response) => response.data.moments,
    }),

    // ========================================================================
    // Grading-id-scoped endpoints (/performance/gradings/:gradingId/...)
    //
    // Canonical, split-aware variants of the tuple endpoints above. Components
    // identify a grading by its id; teammate/account/playbook are derived
    // server-side from the grading row.
    // ========================================================================

    playbookGrading: builder.query<
      TeammateAccountGradingWithRecordings,
      { gradingId: GradingId },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ gradingId }) => ({
        method: "GET",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}`,
      }),
      transformResponse: (response) => response.data.grading,
      providesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookGradingOtherGradings: builder.query<
      {
        otherGradings: readonly TeammateAccountGradingWithRecordings[];
        basicTeammates: readonly BasicTeammate[];
      },
      { gradingId: GradingId; limit?: number },
      {
        data: {
          other_gradings: readonly TeammateAccountGradingWithRecordings[];
          basic_teammates: readonly BasicTeammate[];
        };
      }
    >({
      query: ({ gradingId, limit }) => ({
        method: "GET",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/other-gradings`,
        params: {
          limit: limit ?? undefined,
        },
      }),
      transformResponse: (response) => ({
        otherGradings: response.data.other_gradings,
        basicTeammates: response.data.basic_teammates,
      }),
      providesTags: (result) =>
        result
          ? result.otherGradings.map((g) =>
              buildTeammatePerformanceGradingCacheTag(g.id),
            )
          : [],
    }),

    playbookGradingReassess: builder.mutation<
      TeammateAccountGradingWithRecordings,
      { gradingId: GradingId },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ gradingId }) => ({
        method: "POST",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/reassess`,
      }),
      transformResponse: (response) => response.data.grading,
      invalidatesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookGradingSaveValidation: builder.mutation<
      void,
      {
        gradingId: GradingId;
        sectionOrCriteriaId: string;
        paragraphs: Paragraph[];
      }
    >({
      query: ({ gradingId, sectionOrCriteriaId, paragraphs }) => ({
        method: "POST",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/save-grade-validation`,
        body: {
          section_or_criteria_id: sectionOrCriteriaId,
          paragraphs,
        },
      }),
      invalidatesTags: (_result, _error, { gradingId }) => [
        buildTeammatePerformanceGradingCacheTag(gradingId),
      ],
    }),

    playbookGradingVoiceAgentCallFeedback: builder.mutation<
      TeammateAccountGradingWithRecordings,
      {
        gradingId: GradingId;
        feedback: TeammateAccountGradingFeedback["voice_agent_call"];
      },
      { data: { grading: TeammateAccountGradingWithRecordings } }
    >({
      query: ({ gradingId, feedback }) => ({
        method: "POST",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/voice-agent-call-feedback`,
        body: feedback,
      }),
      transformResponse: (response) => response.data.grading,
      invalidatesTags: (result) =>
        result ? [buildTeammatePerformanceGradingCacheTag(result.id)] : [],
    }),

    playbookGradingCallOutline: builder.query<
      TeammateAccountCallOutline,
      { gradingId: GradingId },
      { data: { call_outline: TeammateAccountCallOutline } }
    >({
      query: ({ gradingId }) => ({
        method: "GET",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/call-outline`,
      }),
      transformResponse: (response) => response.data.call_outline,
      providesTags: (_result, _error, { gradingId }) => [
        {
          type: TagType.TeammateCallOutline,
          id: gradingId,
        },
      ],
    }),

    playbookGradingCustomFields: builder.query<
      CustomField[],
      { gradingId: GradingId },
      { data: { custom_fields: CustomField[] } }
    >({
      query: ({ gradingId }) => ({
        method: "GET",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/custom-fields`,
      }),
      transformResponse: (response) => response.data.custom_fields,
    }),

    playbookGradingInteractionMoments: builder.query<
      readonly InteractionMomentAdminApp[],
      { gradingId: GradingId },
      { data: { moments: readonly InteractionMomentAdminApp[] } }
    >({
      query: ({ gradingId }) => ({
        method: "GET",
        url: `/performance/gradings/${encodeURIComponent(gradingId)}/interaction-moments`,
      }),
      transformResponse: (response) => response.data.moments,
    }),

    playbookGradingValidateGrade: streamQuery<
      { validation: string },
      { gradingId: GradingId; sectionOrCriteriaId: string }
    >(builder, {
      paramsToPath({ gradingId }) {
        return `/performance/gradings/${encodeURIComponent(gradingId)}/validate-grade-stream`;
      },
      paramsToBody({ sectionOrCriteriaId }) {
        return { section_or_criteria_id: sectionOrCriteriaId };
      },
      initialState: { validation: "" },
      updateState(data, chunk) {
        if (typeof chunk !== "string") {
          throw new Error("Expected chunk to be a string");
        }

        return { validation: data.validation + chunk };
      },
      keepUnusedDataFor: 300,
    }),

    playbookGradingHighlightGrade: streamQuery<
      { rawJSON: string; highlights: TextHighlight[] },
      { gradingId: GradingId; gradingValidation: string }
    >(builder, {
      paramsToPath({ gradingId }) {
        return `/performance/gradings/${encodeURIComponent(gradingId)}/grade-highlighting-stream`;
      },
      paramsToBody({ gradingValidation }) {
        return { grading_validation: gradingValidation };
      },
      initialState: { rawJSON: "", highlights: [] },
      updateState(data, chunk) {
        if (typeof chunk !== "string") {
          throw new Error("Expected chunk to be a string");
        }

        const rawJSON = data.rawJSON + chunk;
        const rawJSONSkip = rawJSON.slice(1);

        // Naive partial JSON parsing.
        const highlights: TextHighlight[] = [];
        const highlightRegex = /\{(?:[^{}"\\]|\\.|"(?:\\.|[^"\\])*")*\}/g;

        let match: RegExpExecArray | null;
        while ((match = highlightRegex.exec(rawJSONSkip))) {
          const parsedMatch = safeParseJSONWithSchema(
            match[0],
            textHighlightSchema,
          );
          if (!parsedMatch) {
            continue;
          }

          highlights.push(parsedMatch);
        }

        return {
          rawJSON,
          highlights,
        };
      },
      keepUnusedDataFor: 300,
    }),

    playbookReport: builder.query<
      PlaybookReportResponse,
      {
        playbookId: PlaybookId;
        reportType: PlaybookReportType;
        period: string;
      },
      { data: { report: PlaybookReportResponse } }
    >({
      query: ({ playbookId, reportType, period }) => ({
        method: "GET",
        url: `/performance/playbooks/${encodeURIComponent(playbookId)}/reports/${encodeURIComponent(reportType)}/${encodeURIComponent(period)}`,
      }),
      transformResponse: (response) => response.data.report,
    }),
  }),
});

export const {
  usePlaybookTeamPerformancePageInfiniteQuery,
  usePlaybookTeamPerformanceByGroupPageInfiniteQuery,
  usePlaybookAllMostRecentGradingsCountQuery,
  usePlaybookAllMostRecentGradingsInfiniteQuery,
  usePlaybookTeammateMostRecentKbAdherenceThemesQuery,
  usePlaybookTeammateCoachingPeriodsQuery,
  usePlaybookTeammateCoachingQuery,
  usePlaybookTeammateCoachingGoalCallsQuery,
  usePlaybookTeammateCoachingManagerInputsQuery,
  usePlaybookTeammateSaveCoachingManagerInputMutation,
  usePlaybookTeammateDeleteCoachingManagerInputMutation,
  usePlaybookTeammatePlaybooksQuery,
  usePlaybookTeammatePerformanceQuery,
  usePlaybookTeammatePlaybookCriteriaScoresQuery,
  usePlaybookTeammatePlaybookCriterionCoverageSecondsQuery,
  usePlaybookCriterionCoverageSecondsQuery,
  usePlaybookGlobalAverageScorePerCriteriaQuery,
  usePlaybookOutcomeDistributionPerCriteriaQuery,
  useDashboardPlaybookAttainmentTrendQuery,
  useDashboardPlaybookOutcomeAttainmentTrendQuery,
  usePlaybookDashboardTimeMetricsQuery,
  usePlaybookAverageScorePerTeammateForCriteriaQuery,
  usePlaybookCriteriaAttainmentTrendQuery,
  usePlaybookCriterionTopExamplesQuery,
  usePlaybookSectionAttainmentTrendQuery,
  usePlaybookTeammateMostRecentGradingsCountQuery,
  usePlaybookTeammateMostRecentGradingsInfiniteQuery,
  usePlaybookTeammateGradingQuery,
  usePlaybookGradingByIdQuery,
  usePlaybookOtherTeammateAccountGradingsForAccountQuery,
  usePlaybookTeammateReassessGradingMutation,
  usePlaybookTeammateSaveGradingValidationMutation,
  usePlaybookTeammateAccountVoiceAgentCallFeedbackMutation,
  usePlaybookTeammateValidateGradeQuery,
  useLazyPlaybookTeammateHighlightGradeQuery,
  usePlaybookTeammateCallOutlineQuery,
  usePlaybookTeammateAccountGradingCustomFieldsQuery,
  usePlaybookGradingQuery,
  usePlaybookGradingOtherGradingsQuery,
  usePlaybookGradingReassessMutation,
  usePlaybookGradingSaveValidationMutation,
  usePlaybookGradingVoiceAgentCallFeedbackMutation,
  usePlaybookGradingValidateGradeQuery,
  useLazyPlaybookGradingHighlightGradeQuery,
  usePlaybookGradingCallOutlineQuery,
  usePlaybookGradingCustomFieldsQuery,
  usePlaybookGradingInteractionMomentsQuery,
  usePlaybookTeammatePerformanceAchievementsQuery,
  usePlaybookReportPeriodsQuery,
  usePlaybookReportQuery,
  usePlaybookHighImpactCriteriaQuery,
  useTeammateHighImpactCriteriaQuery,
  useDashboardCriteriaAttainmentTrendsQuery,
  usePlaybookTeammateInteractionMomentsQuery,
} = performancePlaybookApi;

/**
 * The per-interaction cache tag. RTK matches tags on the exact `(type, id)`
 * pair, so a mutation that changes one interaction has to invalidate this as
 * well as the list tag — the list tag alone leaves the detail entry stale.
 */
export function buildTeammatePerformanceGradingCacheTag(gradingId: GradingId) {
  return {
    type: TagType.TeammatePerformanceGrading,
    id: gradingId,
  };
}
